import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  ArrowLeftRight,
  Ban,
  ClipboardList,
  History,
  MapPin,
  PencilLine,
  ShieldCheck,
  Trash2,
  Undo2,
  UserCheck,
} from 'lucide-react';
import { Modal } from '../../../components/ui/Modal';
import { ChangeHistory } from '../../../components/ui/ChangeHistory';
import { getVehicleTransferLogs } from '../../../db/audit-store';
import { findEligibleDrivers } from '../../../core/driver-matcher';
import { hasPermission } from '../../../core/permissions';
import { vehicleCallSign } from '../../../core/military-id';
import {
  VEHICLE_LOCATION_ICONS,
  VEHICLE_LOCATION_STYLES,
  describeVehicleLocation,
} from '../../../core/vehicle-location';
import { soldierRoleLabel } from '../../../core/soldier-roles';
import { AUDIT_ACTION_LABELS } from '../../../types/audit';
import type { AuditLogEntry } from '../../../types/audit';
import {
  VEHICLE_CATEGORY_DESCRIPTIONS,
  VEHICLE_CATEGORY_LABELS,
  VEHICLE_STATUS_LABELS,
  VEHICLE_TYPE_LABELS,
  WEIGHT_CATEGORY_LABELS,
} from '../../../types/fleet';
import type { Soldier, Vehicle } from '../../../types/fleet';
import type { User } from '../../../types/user';
import { VEHICLE_STATUS_STYLES } from './VehicleCard';

interface VehicleDetailsModalProps {
  vehicle: Vehicle;
  soldiers: readonly Soldier[];
  user: User;
  onClose: () => void;
  onEdit: (vehicle: Vehicle) => void;
  /** Hands the vehicle to the roster's delete confirmation. */
  onDelete?: (vehicle: Vehicle) => void;
  /** "החזר לשינוע" — the roster writes it and re-reads. */
  onReturnToBase?: (vehicle: Vehicle) => void;
  /** "השאלה לפלוגה" — the roster opens the transfer dialog. */
  onLoan?: (vehicle: Vehicle) => void;
}

function Field({ label, value, ltr }: { label: string; value: string; ltr?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-ink/50">{label}</dt>
      <dd className="text-sm" data-dir={ltr ? 'ltr' : undefined}>
        {value}
      </dd>
    </div>
  );
}

/** `12/06/2026 08:30` — the loan history reads in the ledger's own format. */
const stamp = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

function SectionTitle({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <h3 className="font-semibold text-sm flex items-center gap-2 mb-2">
      {icon}
      {children}
    </h3>
  );
}

/**
 * Everything known about one vehicle, including who may take it out. The
 * driver lists come from the matching engine — this screen never re-decides
 * eligibility on its own.
 *
 * This is where the roster's compact row unfolds: the full מספר צ, the licence
 * weight class, the exact holding company, the loan history, both transfers and
 * the delete control all live here rather than on the card, so the list itself
 * can stay short enough to read a pool at a glance.
 */
export function VehicleDetailsModal({
  vehicle,
  soldiers,
  user,
  onClose,
  onEdit,
  onDelete,
  onReturnToBase,
  onLoan,
}: VehicleDetailsModalProps) {
  const status = VEHICLE_STATUS_STYLES[vehicle.status];
  const location = describeVehicleLocation(vehicle);
  const locationStyle = VEHICLE_LOCATION_STYLES[location.kind];
  const canManage = hasPermission(user, 'MANAGE_VEHICLES');
  /** Every השאלה and החזרה filed against this vehicle, newest first. */
  const [transfers, setTransfers] = useState<readonly AuditLogEntry[]>([]);

  // The ledger is read once per vehicle the card is opened for. A vehicle that
  // is swapped under the card re-reads; a card that closes drops the result.
  useEffect(() => {
    let live = true;
    void getVehicleTransferLogs(vehicle.id).then((rows) => {
      if (live) setTransfers(rows);
    });
    return () => {
      live = false;
    };
  }, [vehicle.id, vehicle.assignedCompany]);

  // Planning view: who holds the licence for this vehicle, free or not.
  const eligible = findEligibleDrivers(soldiers, vehicle, { ignoreAvailability: true });
  const byId = new Map(soldiers.map((soldier) => [soldier.id, soldier]));
  const blocked = vehicle.disallowedSoldierIds
    .map((id) => byId.get(id))
    .filter((soldier): soldier is Soldier => Boolean(soldier));

  return (
    <Modal
      size="lg"
      title={vehicle.name || vehicle.model}
      subtitle={
        <span className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${locationStyle.dot}`} aria-hidden />
          {location.label} · {VEHICLE_CATEGORY_LABELS[vehicle.category]}
        </span>
      }
      onClose={onClose}
      footer={
        canManage ? (
          <>
            {onDelete ? (
              <button
                type="button"
                onClick={() => onDelete(vehicle)}
                className="h-10 px-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-sm font-medium hover:bg-rose-100 flex items-center gap-2 me-auto"
              >
                <Trash2 size={16} aria-hidden />
                מחיקת רכב
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => onEdit(vehicle)}
              className="h-10 px-4 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-700 flex items-center gap-2"
            >
              <PencilLine size={16} aria-hidden />
              עריכת רכב
            </button>
          </>
        ) : null
      }
    >
      <div className="space-y-5">
        <section className="flex items-center gap-4">
          <div
            data-dir="ltr"
            className="shrink-0 w-20 h-20 rounded-2xl bg-brand-50 text-brand-700 flex items-center justify-center text-3xl font-black tabular-nums"
            title={vehicle.militaryId ? 'מספר פנימי' : 'מספר צ — טרם נרשם מספר פנימי'}
          >
            {vehicleCallSign(vehicle)}
          </div>
          <dl className="grid grid-cols-2 gap-3 grow">
            <Field label="מספר צ מלא" value={vehicle.plateNumber} ltr />
            <Field label="דגם" value={vehicle.model} />
            <Field label="סוג רכב" value={VEHICLE_TYPE_LABELS[vehicle.vehicleType]} />
            <Field label="מאגר" value={VEHICLE_CATEGORY_DESCRIPTIONS[vehicle.category]} />
          </dl>
        </section>

        <section className="rounded-2xl border border-line p-4">
          <SectionTitle icon={<MapPin size={16} className="text-brand-500" aria-hidden />}>
            מיקום ושיוך
          </SectionTitle>
          <span
            data-location={location.kind}
            className={`inline-flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-lg border ${locationStyle.chip}`}
          >
            <span aria-hidden>{VEHICLE_LOCATION_ICONS[location.kind]}</span>
            {location.label}
          </span>
          <dl className="mt-3 grid grid-cols-2 gap-3">
            <Field label="מחזיקה כעת" value={location.company} />
            <Field label="יחידת אם" value={vehicle.company} />
          </dl>
          <p className="mt-3 text-xs text-ink/50 flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${status.dot}`} aria-hidden />
            מצב תפעולי: {VEHICLE_STATUS_LABELS[vehicle.status]}
          </p>
          {onReturnToBase || onLoan ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {onReturnToBase && location.kind !== 'home' ? (
                <button
                  type="button"
                  onClick={() => onReturnToBase(vehicle)}
                  className="h-9 px-3 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-medium hover:bg-emerald-100 flex items-center gap-1.5"
                >
                  <Undo2 size={15} aria-hidden />
                  החזר לשינוע
                </button>
              ) : null}
              {onLoan ? (
                <button
                  type="button"
                  onClick={() => onLoan(vehicle)}
                  className="h-9 px-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm font-medium hover:bg-amber-100 flex items-center gap-1.5"
                >
                  <ArrowLeftRight size={15} aria-hidden />
                  השאלה לפלוגה
                </button>
              ) : null}
            </div>
          ) : null}
        </section>

        <section className="rounded-2xl border border-line p-4">
          <SectionTitle icon={<ShieldCheck size={16} className="text-brand-500" aria-hidden />}>
            רישיון נדרש
          </SectionTitle>
          <div className="flex flex-wrap gap-1.5">
            <span className="text-xs px-2 py-1 rounded-lg border border-line bg-canvas">
              {WEIGHT_CATEGORY_LABELS[vehicle.weightCategory]}
            </span>
          </div>
          <p className="mt-2 text-xs text-ink/50">
            השיבוץ נקבע לפי דרגת המשקל ברישיון הנהיגה הצבאי בלבד.
          </p>
        </section>

        <section className="rounded-2xl border border-line p-4">
          <SectionTitle icon={<UserCheck size={16} className="text-emerald-600" aria-hidden />}>
            נהגים מורשים ({eligible.length})
          </SectionTitle>
          {eligible.length === 0 ? (
            <p className="text-sm text-ink/50">אין חייל בעל רישיון מתאים לרכב זה.</p>
          ) : (
            <ul className="space-y-1.5">
              {eligible.map((match) => {
                const soldier = byId.get(match.soldierId);
                if (!soldier) return null;
                return (
                  <li
                    key={match.soldierId}
                    className="flex items-center justify-between gap-3 text-sm rounded-xl border border-line px-3 py-2"
                  >
                    <span className="truncate">
                      <span className="font-medium">{soldier.fullName}</span>
                      <span className="text-ink/50">
                        {' '}
                        · {soldierRoleLabel(soldier.role)} · {soldier.platoon}
                      </span>
                    </span>
                    <span className="shrink-0 text-xs text-ink/50" data-dir="ltr">
                      {soldier.personalNumber} · {match.score}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="rounded-2xl border border-line p-4">
          <SectionTitle icon={<Ban size={16} className="text-rose-600" aria-hidden />}>
            מוחרגים מהרכב ({blocked.length})
          </SectionTitle>
          {blocked.length === 0 ? (
            <p className="text-sm text-ink/50">לא הוחרגו חיילים מרכב זה.</p>
          ) : (
            <ul className="space-y-1.5">
              {blocked.map((soldier) => (
                <li
                  key={soldier.id}
                  className="text-sm rounded-xl border border-rose-200 bg-rose-50 text-rose-800 px-3 py-2"
                >
                  {soldier.fullName} · <span data-dir="ltr">{soldier.personalNumber}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-2xl border border-line p-4">
          <SectionTitle icon={<History size={16} className="text-amber-600" aria-hidden />}>
            היסטוריית השאלות ({transfers.length})
          </SectionTitle>
          {transfers.length === 0 ? (
            <p className="text-sm text-ink/50">הרכב טרם הושאל לפלוגה אחרת.</p>
          ) : (
            <ul className="space-y-1.5">
              {transfers.map((entry) => (
                <li
                  key={entry.id}
                  className="flex items-start justify-between gap-3 text-sm rounded-xl border border-line px-3 py-2"
                >
                  <span className="min-w-0">
                    <span className="font-medium">{AUDIT_ACTION_LABELS[entry.action]}</span>
                    <span className="block text-xs text-ink/60">{entry.summary}</span>
                  </span>
                  <span
                    className="shrink-0 text-xs text-ink/50 tabular-nums whitespace-nowrap"
                    data-dir="ltr"
                  >
                    {stamp(entry.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Who moved this vehicle's status, and when — read off the ledger.
            `updatedAt` is the revision: a status changed while the card is open
            re-reads it. */}
        <ChangeHistory
          entityType="vehicle"
          entityId={vehicle.id}
          revision={vehicle.updatedAt}
          emptyText="טרם בוצעו שינויים ברכב זה."
        />

        <section className="rounded-2xl border border-line p-4">
          <SectionTitle icon={<ClipboardList size={16} className="text-ink/50" aria-hidden />}>
            היסטוריית משימות
          </SectionTitle>
          {/* The tasks layer lands in a later task; the section is wired and empty. */}
          <p className="text-sm text-ink/50">טרם בוצעו משימות עם רכב זה.</p>
        </section>

        {vehicle.notes ? (
          <section className="rounded-2xl border border-line p-4">
            <SectionTitle icon={<PencilLine size={16} className="text-ink/50" aria-hidden />}>
              הערות
            </SectionTitle>
            <p className="text-sm text-ink/70 whitespace-pre-line">{vehicle.notes}</p>
          </section>
        ) : null}
      </div>
    </Modal>
  );
}
